// src/components/EditProfileForm.js
import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config/config';
import LoadingModal from '../modals/LoadingModal';
import defaultProfile from '../../assets/img/default-profile.jpg';

const EditProfileForm = ({ user, onCancel, updateUser }) => {
  const { currentUser, checkAuthStatus } = useAuth();
  const [formData, setFormData] = useState({
    location: user.location || '',
    business_name: user.business?.business_name || '',
    business_contact_name: user.business?.business_contact_name || '',
    business_type: user.business?.business_type || '',
    business_location: user.business?.business_location || '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileImage, setProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    user.profile_image 
      ? `${config.UPLOADS}/profiles/${user.profile_image}`
      : defaultProfile
  );
  
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const formDataToSend = new FormData();
    formDataToSend.append('userId', user.id);
    formDataToSend.append('location', formData.location);
    
    if (user.user_type === 'event_promoter') {
      formDataToSend.append('business_name', formData.business_name || '');
      formDataToSend.append('business_contact_name', formData.business_contact_name || '');
      formDataToSend.append('business_type', formData.business_type || '');
      formDataToSend.append('business_location', formData.business_location || '');
    }
    
    if (profileImage) {
      formDataToSend.append('profile_image', profileImage);
    }

    try {
      // Get token directly from currentUser object
      const token = currentUser?.token || '';
      
      if (!token) {
        throw new Error('Authentication token not found');
      }


      const response = await axios.post(
        `${config.BASE_URL}/api/auth/update-profile`, 
        formDataToSend, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
          // Don't set Content-Type for FormData
        }
      );

      // After successful update, update previewUrl if profile_image changed
      if (response.data && response.data.success) {
        const updatedProfile = {
          ...user,
          ...response.data.updatedUser,
        };

        if (typeof updateUser === 'function') {
          updateUser(updatedProfile);
        }

        // Update in localStorage
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
          localStorage.setItem('user', JSON.stringify({
            ...storedUser,
            ...updatedProfile
          }));
        }

        // Update preview if new image
        if (updatedProfile.profile_image) {
          setPreviewUrl(`${config.IMAGE_URL}/profiles/${updatedProfile.profile_image}`);
        }

        await checkAuthStatus();
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setTimeout(() => {
          setLoading(false);
          onCancel();
        }, 1500);
      } else {
        setMessage({ type: 'error', text: 'Error updating profile: ' + (response.data?.message || 'Unknown error') });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'An error occurred while updating the profile.' });
      setLoading(false);
    }
  };

  // Fix date display (if you display a date anywhere in this component)
  // Example usage for correct date display:
  // Use this utility to display correct date (fix timezone offset)
  const formatDateLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Add timezone offset to correct for UTC-to-local conversion
    const localDate = new Date(date.getTime() + Math.abs(date.getTimezoneOffset() * 60000));
    return localDate.toISOString().split('T')[0];
  };

  return (
    <section id="edit-profile-form" className="pt-5 pb-5 pt-4" style={{ paddingTop: '120px' }}>
      <div className="container-xl">
        <div className="row">
          <div className="col-md-8 offset-md-2">
            <div className="card bg_grey border-0 shadow">
              <div className="card-header bg-transparent border-0">
                <h3 className="text-white mb-0">Edit Profile</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveChanges}>
                  <div className="row mb-4">
                    <div className="col-md-12 text-center">
                      <div className="profile-image-container position-relative">
                        <img 
                          src={previewUrl}
                          alt="Profile" 
                          className="rounded-circle profile-image" 
                          style={{ width: '150px', height: '150px', objectFit: 'cover', border: '3px solid #ff3636' }}
                        />
                        <div 
                          className="image-overlay rounded-circle"
                          onClick={triggerFileInput}
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: '50%', 
                            transform: 'translateX(-50%)',
                            width: '150px', 
                            height: '150px',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            ':hover': { opacity: 1 }
                          }}
                        >
                          <i className="fa fa-camera fa-2x text-white"></i>
                        </div>
                        <input 
                          type="file"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute"
                          onClick={triggerFileInput}
                          style={{ bottom: '10px', right: '50%', transform: 'translateX(60px)' }}
                        >
                          <i className="fa fa-pencil me-1"></i> Change Photo
                        </button>
                      </div>
                      <h4 className="text-white pt-3">
                        {user.user_type === 'event_promoter' ? (formData.business_name || user.business?.business_name || user.name) : user.name}
                      </h4>
                      <p className="text-muted">{user.email}</p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label text-white text-start d-block">Email</label>
                      <input id="edit-email" className="form-control bg-dark text-white" value={user.email} disabled />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-white text-start d-block">User Type</label>
                      <input id="edit-user_type" className="form-control bg-dark text-white" value={user.user_type || ''} disabled />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label text-white text-start d-block">Location</label>
                      <input
                        id="edit-location"
                        name="location"
                        className="form-control bg-dark text-white"
                        value={formData.location}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Business Information */}
                  {user.user_type === 'event_promoter' && (
                    <>
                      <h5 className="text-white mt-4 mb-3">Business Information</h5>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white text-start d-block">Business Name</label>
                          <input
                            id="edit-business_name"
                            name="business_name"
                            className="form-control bg-dark text-white"
                            value={formData.business_name}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-white text-start d-block">Business Contact Name</label>
                          <input
                            id="edit-business_contact_name"
                            name="business_contact_name"
                            className="form-control bg-dark text-white"
                            value={formData.business_contact_name}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white text-start d-block">Business Type</label>
                          <input
                            id="edit-business_type"
                            name="business_type"
                            className="form-control bg-dark text-white"
                            value={formData.business_type}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-white text-start d-block">Business Location</label>
                          <input
                            id="edit-business_location"
                            name="business_location"
                            className="form-control bg-dark text-white"
                            value={formData.business_location}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-between mt-4">
                    <button type="button" className="btn btn-outline-danger" onClick={onCancel}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>

                {/* Messages */}
                {loading && <LoadingModal message="Updating profile..." />}
                {message.type === 'success' && (
                  <div className="alert alert-success pt-3">{message.text}</div>
                )}
                {message.type === 'error' && (
                  <div className="alert alert-danger pt-3">{message.text}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditProfileForm;
